import React, { useRef, useEffect, useState } from "react";
import { select, interpolate } from "d3";
import { scaleOrdinal } from "d3-scale";
import { hsl } from "d3-color";
import { pie, arc } from "d3-shape";
import { useResizeObserver } from "../../utility/useResizeObserver";
import { fixCountryName } from "../../utility/formatCountry";

export const PieChart = ({ data, property, setTitle, year, country, hidden, selectedTypology: propSelectedTypology, onTypologyChange }) => {
    const svgRef = useRef();
    const wrapperRef = useRef();
    const legendRef = useRef();
    const tooltipRef = useRef();

    // State to control the selected typology
    const [selectedTypology, setSelectedTypology] = useState(propSelectedTypology || "All");
    const [typologyArray, setTypologyArray] = useState([]);
    // State for the chart title.
    const [barLabel, setBarLabel] = useState("");
    // State for container height (used when hidden is true)
    const [chartHeight, setChartHeight] = useState(0);

    const dimensions = useResizeObserver(wrapperRef);
    const margin = { top: 150, right: 50, bottom: 70, left: 200 };

    // Extract typologies from the data array
    useEffect(() => {
        if (!data || data.length === 0) return;
        const typologySet = new Set();
        data.forEach((entry) => {
            Object.keys(entry).forEach((key) => {
                if (key !== "Country" && key !== "Year") {
                    typologySet.add(key);
                }
            });
        });
        const typologyKeys = Array.from(typologySet);
        setTypologyArray(typologyKeys);
        if (!typologyKeys.includes(selectedTypology) && selectedTypology !== "All") {
            setSelectedTypology("All");
        }
    }, [data, selectedTypology]);

    // Handle prop changes for export
    useEffect(() => {
        if (propSelectedTypology && propSelectedTypology !== selectedTypology) {
            setSelectedTypology(propSelectedTypology);
        }
    }, [propSelectedTypology, selectedTypology]);

    // Main rendering
    useEffect(() => {
        const { width, height } = dimensions || wrapperRef.current.getBoundingClientRect();
        if (!dimensions) return;

        select(svgRef.current).selectAll("*").remove();
        select(legendRef.current).selectAll("*").remove();
        setBarLabel("");

        if (!data || data.length === 0) {
            select(svgRef.current)
                .append("text")
                .attr("text-anchor", "middle")
                .attr("x", (width + margin.left + margin.right) / 2)
                .attr("y", (height + margin.top + margin.bottom) / 2)
                .attr("font-size", "20px")
                .attr("fill", "#ff0000")
                .text("No data available");
            return;
        }

        const newBarLabel = getDynamicBarTitle(selectedTypology, property, year, country);
        setBarLabel(newBarLabel);
        setTitle(newBarLabel);

        const svg = select(svgRef.current)
            .attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom);

        //set margins and sizes for export
        let radius, marginHorizontal;

        if (hidden) {
            radius = Math.min(width - margin.left - margin.right, height - margin.top - margin.bottom) / 2.5;
            marginHorizontal = margin.left + (width - margin.left - margin.right) / 1.5;
        } else {
            radius = Math.min(width - margin.left - margin.right, height - margin.top - margin.bottom) / 1.9;
            marginHorizontal = margin.left + (width - margin.left - margin.right) / 1.3;
        }

        // Adjust group position for margins
        const chartGroup = svg
            .append("g")
            .attr("transform", `translate(${marginHorizontal}, ${(height + margin.top - margin.bottom) / 2})`);

        let chartData = [];
        if (selectedTypology === "All") {
            data.forEach((entry) => {
                const countryName = fixCountryName(entry.Country);
                Object.keys(entry).forEach((key) => {
                    if (key !== "Country" && key !== "Year") {
                        const value = parseFloat(entry[key]) || 0;
                        if (value > 0) {
                            chartData.push({
                                label: `${countryName} - ${key}`,
                                country: countryName,
                                typology: key,
                                value: value,
                            });
                        }
                    }
                });
            });
        } else {
            data.forEach((entry) => {
                const countryName = fixCountryName(entry.Country);
                const value = parseFloat(entry[selectedTypology]) || 0;
                if (value > 0) {
                    chartData.push({
                        label: `${countryName} - ${selectedTypology}`,
                        country: countryName,
                        typology: selectedTypology,
                        value: value,
                    });
                }
            });
        }

        if (chartData.length === 0) {
            svg
                .append("text")
                .attr("text-anchor", "middle")
                .attr("font-size", "20px")
                .attr("fill", "#ff0000")
                .text("No data available");
            return;
        }

        // Get the unique list of countries
        const uniqueCountries = Array.from(new Set(chartData.map(d => d.country)));

        // Create a mapping for base colours using HSL with evenly spaced hues
        const countryColorMap = uniqueCountries.reduce((map, country, index) => {
            const hue = index * (360 / uniqueCountries.length);
            map[country] = hsl(hue, 0.7, 0.5);
            return map;
        }, {});

        // Adjust each datum’s colour based on typology variant
        const countryCounts = {};
        chartData = chartData.map(d => {
            if (!countryCounts[d.country]) {
                countryCounts[d.country] = 0;
            }
            const variantIndex = countryCounts[d.country];
            countryCounts[d.country]++;
            const baseColor = countryColorMap[d.country];
            const variantColor = variantIndex > 0 ? baseColor.brighter(variantIndex * 0.3) : baseColor;
            return { ...d, color: variantColor.toString() };
        });

        const pieGenerator = pie().value((d) => d.value).padAngle(0.02);

        const arcGenerator = arc().innerRadius(radius * 0.3).outerRadius(radius).cornerRadius(5);

        const tooltip = select(tooltipRef.current)
            .style("opacity", 0)
            .style("visibility", "visible")
            .attr("class", "tooltip");

        const arcs = chartGroup.selectAll("g.arc").data(pieGenerator(chartData), (d) => d.data.label);

        arcs.exit()
            .transition()
            .duration(750)
            .attrTween("d", function (d) {
                const i = interpolate(d, { startAngle: d.endAngle, endAngle: d.endAngle });
                return (t) => arcGenerator(i(t));
            })
            .remove();

        const enteringArcs = arcs.enter()
            .append("g")
            .attr("class", "arc")
            .style("cursor", "pointer");

        enteringArcs.append("path")
            .attr("fill", d => d.data.color)
            .attr("d", arcGenerator)
            .transition()
            .duration(1000)
            .attrTween("d", function (d) {
                const i = interpolate({ startAngle: 0, endAngle: 0 }, d);
                return (t) => arcGenerator(i(t));
            });

        enteringArcs.selectAll("path")
            .on("mouseover", (event, d) => {
                tooltip.text(`${d.data.label}: ${d.data.value.toFixed(2)}`).style("opacity", 1);
                enteringArcs.selectAll("path").style("opacity", 0.5);
                select(event.target).style("opacity", 1);
            })
            .on("mousemove", (event) => {
                const wrapperPosition = wrapperRef.current.getBoundingClientRect();
                tooltip
                    .style("left", `${event.clientX - wrapperPosition.left + 10}px`)
                    .style("top", `${event.clientY - wrapperPosition.top + 10}px`);
            })
            .on("mouseout", () => {
                tooltip.style("opacity", 0);
                enteringArcs.selectAll("path").style("opacity", 1);
            });

        arcs.select("path")
            .transition()
            .duration(750)
            .attr("d", arcGenerator);

        const legend = select(legendRef.current)
            .attr("width", width)
            .attr("height", 50 * chartData.length);
        legend.selectAll(".legend-item").remove();

        const legendItem = legend.selectAll(".legend-item").data(chartData);
        const enteringLegendItems = legendItem.enter()
            .append("div")
            .attr("class", "legend-item")
            .on("mouseover", function (event, d) {
                tooltip.text(`${d.label}: ${d.value.toFixed(2)}`).style("opacity", 1);
                chartGroup.selectAll("g.arc path")
                    .filter((sliceData) => sliceData.data.label === d.label)
                    .style("opacity", 1);
                chartGroup.selectAll("g.arc path")
                    .filter((sliceData) => sliceData.data.label !== d.label)
                    .style("opacity", 0.5);
                legend.selectAll(".legend-item").style("opacity", 0.5);
                select(this).style("opacity", 1);
            })
            .on("mousemove", function (event, d) {
                const wrapperPosition = wrapperRef.current.getBoundingClientRect();
                tooltip
                    .style("left", `${event.clientX - wrapperPosition.left + 10}px`)
                    .style("top", `${event.clientY - wrapperPosition.top + 10}px`);
            })
            .on("mouseout", function () {
                chartGroup.selectAll("g.arc path").style("opacity", 1);
                legend.selectAll(".legend-item").style("opacity", 1);
                tooltip.style("opacity", 0);
            });

        enteringLegendItems.append("div")
            .style("background-color", d => d.color)
            .style("width", "12px")
            .style("height", "12px")
            .style("margin-right", "5px")
            .style("border-radius", '3px');

        enteringLegendItems.append("p")
            .text((d) => d.label);

        // *** Add value labels outside the pie for export ***
        if (hidden) {
            const maxVal = Math.max(...chartData.map(d => d.value));
            const sliceCount = chartData.length;
            const totalValue = chartData.reduce((sum, d) => sum + d.value, 0);

            // Calculate threshold for showing labels (e.g., 2% of total)
            const threshold = totalValue * 0.02; // 2% threshold
            const significantSlices = chartData.filter(d => d.value >= threshold);
            const smallSlices = chartData.filter(d => d.value < threshold);
            const smallSlicesValue = smallSlices.reduce((sum, d) => sum + d.value, 0);

            if (significantSlices.length > 0) {
                const maxFont = 14;
                const minFont = 8;

                // Add leader lines and labels only for significant slices
                chartGroup.selectAll("g.arc")
                    .filter(function (d) {
                        return d.data.value >= threshold;
                    })
                    .each(function (d) {
                        const arcGroup = select(this);

                        // Add leader line
                        arcGroup.append("line")
                            .attr("x1", function () {
                                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                                return Math.cos(midAngle - Math.PI / 2) * (radius + 5);
                            })
                            .attr("y1", function () {
                                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                                return Math.sin(midAngle - Math.PI / 2) * (radius + 5);
                            })
                            .attr("x2", function () {
                                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                                return Math.cos(midAngle - Math.PI / 2) * (radius + 25);
                            })
                            .attr("y2", function () {
                                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                                return Math.sin(midAngle - Math.PI / 2) * (radius + 25);
                            })
                            .attr("stroke", "#000")
                            .attr("stroke-width", 0.8);

                        // Add label
                        arcGroup.append("text")
                            .attr("transform", function () {
                                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                                const adjustedAngle = midAngle - Math.PI / 2;
                                const labelRadius = radius + 35;
                                const x = Math.cos(adjustedAngle) * labelRadius;
                                const y = Math.sin(adjustedAngle) * labelRadius;
                                return `translate(${x}, ${y})`;
                            })
                            .attr("text-anchor", function () {
                                const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
                                return midAngle < Math.PI ? "start" : "end";
                            })
                            .attr("fill", "#000")
                            .attr("font-size", function () {
                                const fontSize = minFont + (d.data.value / maxVal) * (maxFont - minFont);
                                return `${Math.max(minFont, Math.min(maxFont, fontSize))}px`;
                            })
                            .text(d.data.value.toFixed(2));
                    });
            }

            if (smallSlices.length > 0) {
                // Calculate available space and position the note properly
                const noteText = `Note: ${smallSlices.length} slice${smallSlices.length > 1 ? 's' : ''} with values < ${threshold.toFixed(2)} (${((smallSlicesValue / totalValue) * 100).toFixed(1)}% total) not labeled for clarity`;

                // Create a temporary text element to measure text dimensions
                const tempText = chartGroup.append("text")
                    .attr("font-size", "10px")
                    .attr("opacity", 0)
                    .text(noteText);

                const textBBox = tempText.node().getBBox();
                tempText.remove();

                // Calculate positioning to keep note within SVG bounds
                const noteWidth = textBBox.width;
                const noteHeight = textBBox.height;

                // Position note below the pie chart with some padding
                const noteX = -noteWidth / 2; // Center horizontally relative to pie center
                const noteY = radius + 80; // Position below the pie with padding

                // Ensure the note fits within the SVG height
                const maxY = (height + margin.top - margin.bottom) / 2 + radius + 100;
                const finalY = Math.min(noteY, maxY - noteHeight - 10);

                const noteContainer = chartGroup.append("g")
                    .attr("class", "small-slices-note")
                    .attr("transform", `translate(${noteX}, ${finalY})`);

                // Add background rectangle for better readability
                noteContainer.append("rect")
                    .attr("x", -5)
                    .attr("y", -noteHeight)
                    .attr("width", noteWidth + 10)
                    .attr("height", noteHeight + 10)
                    .attr("fill", "#f9f9f9")
                    .attr("stroke", "#ddd")
                    .attr("stroke-width", 1)
                    .attr("rx", 3);

                noteContainer.append("text")
                    .attr("font-size", "10px")
                    .attr("fill", "#666")
                    .attr("font-style", "italic")
                    .attr("text-anchor", "start")
                    .text(noteText);
            }
        }
    }, [data, dimensions, selectedTypology, propSelectedTypology]);

    return (
        <>
            <div className="chartTitle">
                <p id={typologyArray.length <= 0 ? "noCategoriesTitle" : ''}>{barLabel}</p>
                <p id='subtitle'>Please note that the following values refer to the projects and plants described in detail in the WGC reporting. They may overlook project data and fail to achieve the actual Grand Total values</p>
                {typologyArray.length > 0 && !hidden && (
                    <select
                        className="input"
                        id="dataCategoriesBar"
                        value={selectedTypology}
                        onChange={(e) => {
                            setSelectedTypology(e.target.value);
                            if (onTypologyChange) {
                                onTypologyChange(e.target.value);
                            }
                        }}
                    >
                        <option value="All">All Categories</option>
                        {typologyArray.map((typology, index) => (
                            <option key={index} value={typology}>
                                {typology}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            <div
                ref={wrapperRef}
                id="chartContainer"
                style={{ position: "relative", height: hidden ? '90vh' : "100%" }}
            >
                <svg ref={svgRef} className="chartSvg"></svg>
                <div ref={tooltipRef}></div>
                <div ref={legendRef} className="chartLegend" style={{ overflowY: hidden ? 'visible' : 'scroll', width: hidden ? '15%' : '40%', flexWrap: hidden ? 'wrap' : 'nowrap' }}></div>
            </div>
        </>
    );
};

function getDynamicBarTitle(selectedTypology, property, year, country) {
    const countryTitle =
        country
            .map((countryName, index) =>
                countryName.label + (index === country.length - 1 ? "" : ", ")
            )
            .join("");
    switch (property) {
        case 'Pie':
            return `Gross Energy Produced of ${selectedTypology} as declared for geothermal power plants (MWt) in ${countryTitle} for WGC ${year}`;
        case 'Total Gross Electrical generation GWh/yr':
            return `Total Gross Electrical Generation GWh/year in ${countryTitle} for WGC ${year}`;
        case 'Total Installed Capacity (MWe)':
            return `Total Installed Capacity (MWe) in ${countryTitle} for WGC ${year}`;
        case 'Total Energy Used (TJ/year)':
            return `Total Energy Used (TJ/year) in ${countryTitle} for WGC ${year}`;
        case 'Total Installed Capacity (MWt)':
            return `Total Installed Capacity (MWt) in ${countryTitle} for WGC ${year}`;
        case 'Gross Energy Produced (GEP) (GWh/year)':
            return `Gross Energy Produced (GWh/year) of ${selectedTypology} as declared for geothermal power plants (MWt) in ${countryTitle} for WGC ${year}`;
        case 'Installed Capacity (MW)':
            return `Installed Capacity of ${selectedTypology} as declared for geothermal power plants (MWt) in ${countryTitle} for WGC ${year}`;
        case 'Energy used (net) (TJ/year)':
            return `Energy Used (TJ/year) of ${selectedTypology} as declared for geothermal Heating and Cooling categories in ${countryTitle} for WGC ${year}`;
        case 'Nominal Capacity (MWt)':
            return `Installed Capacity (MWt) of ${selectedTypology} as declared for geothermal  Heating and Cooling categories in ${countryTitle} for WGC ${year}`;
        default:
            return `${property} of ${selectedTypology} in ${countryTitle} for WGC ${year}`;
    }
}